import React, { useState, useEffect } from 'react';
import { ref, get, remove } from 'firebase/database';
import { database } from '../../firebase/config';
import { formattaData } from '../../utils/dateUtils';
import { deleteImmagine } from '../../utils/cloudinaryUtils';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';

// ─── STILI PDF (identici a CreaBollaForm) ────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page:        { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  title:       { fontSize: 16, fontWeight: 'bold', textAlign: 'center', marginBottom: 16 },
  section:     { marginBottom: 10 },
  row:         { flexDirection: 'row', marginBottom: 4 },
  label:       { fontWeight: 'bold', width: 160 },
  value:       { flex: 1 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#e0e0e0', padding: 4, marginBottom: 2, fontWeight: 'bold' },
  tableRow:    { flexDirection: 'row', padding: 4, borderBottom: '1 solid #ccc' },
  col1:        { width: '40%' },
  col2:        { width: '30%' },
  col3:        { width: '30%' },
  divider:     { borderBottom: '1 solid #aaa', marginVertical: 8 },
  firmaImg:    { width: 200, height: 80, border: '1 solid #999' },
  footerRow:   { flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 },
  footerCol:   { width: '45%' },
  footerLabel: { fontWeight: 'bold', marginBottom: 4 },
  prestazione: { marginTop: 4, lineHeight: 1.5 },
  // Intestazione con logo e recapiti
  intestazione:     { flexDirection: 'row', alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottom: '1 solid #ccc' },
  logoImg:          { width: 80, height: 32, objectFit: 'contain' },
  recapiti:         { marginLeft: 12, fontSize: 9, color: '#333' },
  recapitiNome:     { fontSize: 10, fontWeight: 'bold', marginBottom: 2 },
  // ── NUOVO: box numero bolla ──────────────────────────────────────────────
  numeroBollaBox:   { marginLeft: 'auto', backgroundColor: '#1a1a1a', borderRadius: 4, paddingVertical: 6, paddingHorizontal: 12, alignItems: 'center', justifyContent: 'center' },
  numeroBollaLabel: { fontSize: 7, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 },
  numeroBollaText:  { fontSize: 20, fontWeight: 'bold', color: '#ffffff' },
  // ────────────────────────────────────────────────────────────────────────
  // Pagina 2 – foto pesata
  page2:          { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  page2Title:     { fontSize: 14, fontWeight: 'bold', textAlign: 'center', marginBottom: 12 },
  page2Subtitle:  { fontSize: 10, color: '#555', textAlign: 'center', marginBottom: 16 },
  fotoPesataImg:  { width: '100%', maxHeight: 620, objectFit: 'contain', border: '1 solid #ccc' },
  fotoPesataBox:  { width: '100%', height: 300, border: '1 solid #ccc', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  fotoPesataNone: { color: '#999', fontSize: 12 },
});

// ─── COMPONENTE PDF (2 pagine) ────────────────────────────────────────────────
function fmtData(d) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function BollaPDF({ dati }) {
  const {
    cliente, cantiere, data, mezzi,
    smaltimentoScavo, smaltimentoDemo,
    prestazione, nomeAutista,
    firmaBase64, fotoPesataUrl, nomeClienteFirma,
    numeroBolla, // ← NUOVO
  } = dati;

  return (
    <Document>
      {/* ── PAGINA 1 – Contenuto bolla ── */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Intestazione: logo + recapiti ditta + NUMERO BOLLA */}
        <View style={pdfStyles.intestazione}>
          <Image src="CRG_logo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso (TI)</Text>
            <Text>Tel: +41 78 751 32 51 - email:info@crgscavi.ch</Text>
          </View>
          {/* ── NUOVO: numero bolla ben visibile ── */}
          <View style={pdfStyles.numeroBollaBox}>
            <Text style={pdfStyles.numeroBollaLabel}>N° BOLLA</Text>
            <Text style={pdfStyles.numeroBollaText}>{numeroBolla ?? '—'}</Text>
          </View>
        </View>

        <Text style={pdfStyles.title}>BOLLA DI LAVORO</Text>

        <View style={pdfStyles.section}>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Cliente</Text>
            <Text style={pdfStyles.value}>{cliente}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Cantiere</Text>
            <Text style={pdfStyles.value}>{cantiere}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Data</Text>
            <Text style={pdfStyles.value}>{fmtData(data)}</Text>
          </View>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>Mezzi utilizzati</Text>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Mezzo</Text>
            <Text style={pdfStyles.col2}>Ore</Text>
            <Text style={pdfStyles.col3}>KM</Text>
          </View>
          {mezzi.filter(m => m.veicoloId).map((m, i) => (
            <View key={i} style={pdfStyles.tableRow}>
              <Text style={pdfStyles.col1}>{m.label}</Text>
              <Text style={pdfStyles.col2}>{m.ore || '-'}</Text>
              <Text style={pdfStyles.col3}>{m.km || '-'}</Text>
            </View>
          ))}
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Smaltimento materiale da scavo (m³)</Text>
            <Text style={pdfStyles.value}>{smaltimentoScavo || '-'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Smaltimento demolizione (m³)</Text>
            <Text style={pdfStyles.value}>{smaltimentoDemo || '-'}</Text>
          </View>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.section}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>Prestazione</Text>
          <Text style={pdfStyles.prestazione}>{prestazione || '-'}</Text>
        </View>

        <View style={pdfStyles.divider} />

        <View style={pdfStyles.footerRow}>
          <View style={pdfStyles.footerCol}>
            <Text style={pdfStyles.footerLabel}>Nome autista</Text>
            <Text>{nomeAutista}</Text>
          </View>
          <View style={pdfStyles.footerCol}>
            <Text style={pdfStyles.footerLabel}>Firma cliente</Text>
            {firmaBase64
              ? <Image src={firmaBase64} style={pdfStyles.firmaImg} />
              : <Text style={{ color: '#999' }}>Non firmato</Text>
            }
            {nomeClienteFirma ? (
              <Text style={{ marginTop: 4, fontSize: 10 }}>{nomeClienteFirma}</Text>
            ) : null}
          </View>
        </View>

      </Page>

      {/* ── PAGINA 2 – Foto pesata (SEMPRE presente) ── */}
      <Page size="A4" style={pdfStyles.page2}>
        <Text style={pdfStyles.page2Title}>FOTO PESATA</Text>
        <Text style={pdfStyles.page2Subtitle}>{cantiere} — {fmtData(data)}</Text>
        <View style={pdfStyles.divider} />

        {fotoPesataUrl
          ? <Image src={fotoPesataUrl} style={pdfStyles.fotoPesataImg} />
          : (
            <View style={pdfStyles.fotoPesataBox}>
              <Text style={pdfStyles.fotoPesataNone}>Nessuna foto pesata allegata</Text>
            </View>
          )
        }
      </Page>
    </Document>
  );
}

// ─── MODAL PRINCIPALE ─────────────────────────────────────────────────────────
function VisualizzaBolleForm({ dipendente, onClose }) {
  const [bolle, setBolle]           = useState([]);
  const [cantieri, setCantieri]     = useState([]);
  const [clienti, setClienti]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filtroData, setFiltroData] = useState('');
  const [generandoPdf, setGenerandoPdf] = useState(null);

  useEffect(() => { caricaDati(); }, [dipendente]);

  async function caricaDati() {
    setLoading(true);
    const [bolleSnap, cantSnap, cliSnap] = await Promise.all([
      get(ref(database, 'bolleForm')),
      get(ref(database, 'cantieri')),
      get(ref(database, 'clienti')),
    ]);
    if (cantSnap.exists()) setCantieri(Object.keys(cantSnap.val()).map(k => ({ id: k, ...cantSnap.val()[k] })));
    if (cliSnap.exists())  setClienti(Object.keys(cliSnap.val()).map(k => ({ id: k, ...cliSnap.val()[k] })));
    if (bolleSnap.exists()) {
      const tutte = Object.keys(bolleSnap.val()).map(k => ({ id: k, ...bolleSnap.val()[k] }));
      const delDipendente = tutte
        .filter(b => b.dipendenteId === dipendente.id)
        .sort((a, b) => b.createdAt - a.createdAt);
      setBolle(delDipendente);
    }
    setLoading(false);
  }

  function getCantiere(id) { return cantieri.find(c => c.id === id); }
  function getCliente(cantiereId) {
    const cant = getCantiere(cantiereId);
    return cant ? clienti.find(c => c.id === cant.clienteId) : null;
  }

  async function scaricaPDF(bolla) {
    setGenerandoPdf(bolla.id);
    try {
      const cliente  = getCliente(bolla.cantiereId);
      const cantiere = getCantiere(bolla.cantiereId);

      const dati = {
        ...bolla,
        cliente:          cliente?.ragioneSociale || bolla.clienteId,
        cantiere:         cantiere?.luogo || bolla.cantiereId,
        firmaBase64:      bolla.firmaUrl,
        fotoPesataUrl:    bolla.fotoPesataUrl || null,
        nomeClienteFirma: bolla.nomeClienteFirma || '',
        numeroBolla:      bolla.numeroBolla ?? '—', // ← NUOVO: letto da Firebase
      };

      const blob = await pdf(<BollaPDF dati={dati} />).toBlob();
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore generazione PDF: ' + err.message);
    } finally {
      setGenerandoPdf(null);
    }
  }

  async function eliminaBolla(id) {
    if (!window.confirm('Eliminare questa bolla?')) return;
    try {
      const bolla = bolle.find(b => b.id === id);
      if (bolla?.firmaPubblicId)      await deleteImmagine(bolla.firmaPubblicId);
      if (bolla?.fotoPesataPubblicId) await deleteImmagine(bolla.fotoPesataPubblicId);
      await remove(ref(database, `bolleForm/${id}`));
      caricaDati();
      alert('Bolla eliminata correttamente');
    } catch (err) {
      alert('Errore eliminazione: ' + err.message);
    }
  }

  const bolleFiltrate = filtroData
    ? bolle.filter(b => b.data === filtroData)
    : bolle;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className="modal-header">
          <h2>Bolle Form — {dipendente.nome} {dipendente.cognome}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* FILTRO DATA */}
        <div className="modal-filtro">
          <label>Filtra per data</label>
          <input
            type="date"
            value={filtroData}
            onChange={e => setFiltroData(e.target.value)}
          />
          {filtroData && (
            <button className="btn-secondary small" onClick={() => setFiltroData('')}>
              Rimuovi filtro
            </button>
          )}
        </div>

        {/* CONTENUTO */}
        <div className="modal-body">
          {loading ? (
            <p className="loading-text">Caricamento...</p>
          ) : bolleFiltrate.length === 0 ? (
            <p className="empty-text">
              {filtroData
                ? `Nessuna bolla per il ${formattaData(filtroData)}`
                : 'Nessuna bolla form compilata'}
            </p>
          ) : (
            <>
              <p className="bolle-count">
                {bolleFiltrate.length} {bolleFiltrate.length === 1 ? 'bolla' : 'bolle'}
              </p>
              <div className="bolle-list">
                {bolleFiltrate.map(bolla => {
                  const cliente  = getCliente(bolla.cantiereId);
                  const cantiere = getCantiere(bolla.cantiereId);

                  return (
                    <div key={bolla.id} className="bolla-item">
                      <div className="bolla-info">
                        {/* ── NUOVO: mostra numero bolla anche nella lista ── */}
                        {bolla.numeroBolla && (
                          <span style={{
                            fontWeight: 'bold', fontSize: 12,
                            background: '#1a1a1a', color: '#fff',
                            borderRadius: 4, padding: '1px 7px', marginRight: 6,
                          }}>
                            #{bolla.numeroBolla}
                          </span>
                        )}
                        <span className="bolla-data">{formattaData(bolla.data)}</span>
                        <span className="bolla-cantiere">{cantiere?.luogo}</span>
                        <span className="bolla-cliente">{cliente?.ragioneSociale}</span>
                        {(bolla.firmaUrl || bolla.firmaBase64) && (
                          <span className="bolla-firmata">Firmata</span>
                        )}
                        {bolla.nomeClienteFirma && (
                          <span style={{ fontSize: 11, color: '#555', marginLeft: 4 }}>
                            👤 {bolla.nomeClienteFirma}
                          </span>
                        )}
                        {bolla.fotoPesataUrl && (
                          <span style={{
                            fontSize: 11, color: '#2e7d32',
                            background: '#e8f5e9', borderRadius: 4,
                            padding: '1px 6px', marginLeft: 4,
                          }}>
                            📷 Foto pesata
                          </span>
                        )}
                      </div>

                      <div className="bolla-azioni">
                        <button
                          className="btn-primary small"
                          onClick={() => scaricaPDF(bolla)}
                          disabled={generandoPdf === bolla.id}
                        >
                          {generandoPdf === bolla.id ? '...' : 'PDF'}
                        </button>
                        <button
                          className="btn-danger small"
                          onClick={() => eliminaBolla(bolla.id)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default VisualizzaBolleForm;